import { useCallback } from 'react';

export default function useSmartInputInsertions({
    inputRef,
    fileTrigger,
    variableTrigger,
    descriptions,
    formatFileInsertion,
    onChange,
    convertDisplayToIds,
    setShowDropdown,
}) {
    const insertFile = useCallback(
        (fileItem) => {
            if (!fileItem || !inputRef.current) return;
            const input = inputRef.current;
            const currentDisplayValue = input.value;
            const cursor = input.selectionStart;
            let before = currentDisplayValue.slice(0, cursor);
            const after = currentDisplayValue.slice(cursor);
            if (fileTrigger && before.endsWith(fileTrigger)) {
                before = before.slice(0, -fileTrigger.length);
            }
            const insertionStored =
                typeof formatFileInsertion === 'function'
                    ? formatFileInsertion(fileItem)
                    : `@[${fileItem.id}]`;
            const beforeStored = convertDisplayToIds(before);
            const afterStored = convertDisplayToIds(after);
            const newStoredValue = beforeStored + insertionStored + afterStored;
            onChange({ target: { value: newStoredValue, name: input.name } });
            setShowDropdown(false);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!input) return;
                    input.focus();
                    const displayInsertLength = fileItem.name?.length ?? insertionStored.length;
                    const newCursor = before.length + displayInsertLength;
                    const safeCursor = Math.min(newCursor, (input.value || '').length);
                    if (safeCursor >= 0) {
                        input.selectionStart = input.selectionEnd = safeCursor;
                    }
                });
            });
        },
        [fileTrigger, formatFileInsertion, onChange, convertDisplayToIds, inputRef, setShowDropdown]
    );

    const insertVariable = useCallback(
        (tokenObj) => {
            if (!tokenObj || !tokenObj.id) return;
            const input = inputRef.current;
            if (!input) return;
            const currentDisplayValue = input.value;
            const cursor = input.selectionStart;
            let before = currentDisplayValue.slice(0, cursor);
            const after = currentDisplayValue.slice(cursor);
            if (before.endsWith(variableTrigger)) {
                before = before.slice(0, -variableTrigger.length);
            }
            const prefix = descriptions[tokenObj.id]?.systemVariable ? 'PROJECT.' : 'ENV.';
            const insertedDisplayText = `\${${prefix}${tokenObj.key}}`;
            const beforeStored = convertDisplayToIds(before);
            const afterStored = convertDisplayToIds(after);
            const storedFormat = `\${_id:${tokenObj.id}}`;
            const newStoredValue = beforeStored + storedFormat + afterStored;
            onChange({ target: { value: newStoredValue, name: input.name } });
            setShowDropdown(false);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!input) return;
                    input.focus();
                    const newCursor = before.length + insertedDisplayText.length;
                    const safeCursor = Math.min(newCursor, (input.value || '').length);
                    if (safeCursor >= 0) {
                        input.selectionStart = input.selectionEnd = safeCursor;
                    }
                });
            });
        },
        [descriptions, onChange, convertDisplayToIds, variableTrigger, inputRef, setShowDropdown]
    );

    return { insertFile, insertVariable };
}
